import { createSelector } from '@ngrx/store';
import { PayRuleStore } from '../reducers/payment-rules.reducer';
import { selectAllowCreditCard } from './payment-rules.selectors';
import {
  PaymentInclusionExclusionSetting,
  RestrictionType,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectOriginatingApplication } from '../../session-store';

export const selectPaymentInclusionExclusionRules = createSelector(
  PayRuleStore,
  (state) => state.paymentInclusionExclusion,
);

export const selectIsPaymentInclusionExclusionRulesNull = createSelector(
  PayRuleStore,
  (state) => state.paymentInclusionExclusion == null,
);

export const selectIsBuyerAccountRestrictionEnabledForOriginSystem =
  createSelector(
    selectOriginatingApplication,
    selectPaymentInclusionExclusionRules,
    (originatingApplication, paymentInclusionExclusion) => {
      const applicationAccountRestriction =
        paymentInclusionExclusion?.applications?.find(
          (restriction) =>
            restriction.originApplication &&
            restriction.originApplication.toUpperCase() ===
              originatingApplication.toUpperCase(),
        );
      return (
        applicationAccountRestriction?.buyerAccountRestrictionEnabled ?? true
      );
    },
  );

export const selectPaymentInclusionExclusionSettings = createSelector(
  selectAllowCreditCard,
  selectPaymentInclusionExclusionRules,
  selectIsBuyerAccountRestrictionEnabledForOriginSystem,
  (
    supplierAcceptsCreditCard,
    configuration,
    isBuyerAccountRestrictionEnabledForOriginSystem,
  ) => {
    const restrictionType = configuration?.restrictionType;
    const paymentInclusionExclusionSetting: PaymentInclusionExclusionSetting = {
      allowACH: true,
      allowCreditCard: supplierAcceptsCreditCard,
    };

    if (
      !isBuyerAccountRestrictionEnabledForOriginSystem ||
      !restrictionType ||
      restrictionType === RestrictionType.NoRestriction ||
      !supplierAcceptsCreditCard
    ) {
      return paymentInclusionExclusionSetting;
    }

    const restrictionConfigurations = configuration.restrictionConfigurations;
    const allBuyerAccountsIncluded = restrictionConfigurations.every(
      (config) => config.restrictionType === RestrictionType.Inclusion,
    );

    const anyBuyerAccountsExcluded = restrictionConfigurations.some(
      (config) => config.restrictionType === RestrictionType.Exclusion,
    );

    // If the restriction type is Exclusion AND any of the buyer accounts are in the exclusion list, then do not show saved credit card payment accounts
    if (restrictionType === RestrictionType.Exclusion) {
      paymentInclusionExclusionSetting.allowCreditCard =
        !anyBuyerAccountsExcluded;
    }
    // If the restriction type is Inclusion AND not all buyer accounts are included then do not show saved credit card payment accounts
    else if (restrictionType === RestrictionType.Inclusion) {
      paymentInclusionExclusionSetting.allowCreditCard =
        allBuyerAccountsIncluded;
    }

    return paymentInclusionExclusionSetting;
  },
);
